import { VSCPartJson } from '../../../interfaces/VersaBuilt.Interface';

type MetricUnit = 'mm' | 'cm';

const PER_INCH: Record<MetricUnit, number> = { mm: 25.4, cm: 2.54 };

// Lengths under `parameters`, in the unit the add-in stores them. Everything
// else there (weights in kg, pressures, angles, speeds, counts, vise numbers,
// the 6-axis transfer poses) is left untouched.
/** Stock width/length/height — must be greater than zero in the form. */
export const STOCK_DIMENSION_KEYS = new Set(['width', 'length', 'partHeight']);

export const LENGTH_PARAMETER_UNITS: Record<string, MetricUnit> = {
	width: 'mm',
	length: 'mm',
	partHeight: 'mm',
	finishedPartHeight: 'mm',
	transferPartHeight: 'mm',
	pickHeight: 'mm',
	placeHeight: 'mm',
	yPushHeight: 'mm',
	op1YPushDistance: 'mm',
	op1YPushXOffset: 'mm',
	infeedXOffset: 'mm',
	infeedYOffset: 'mm',
	outfeedXOffset: 'mm',
	outfeedYOffset: 'mm',
	customStartX: 'mm',
	customStartY: 'mm',
	customXSpacing: 'mm',
	customYSpacing: 'mm',
	op1to2TransferHeight: 'mm',
	op1to2ClearanceHeight: 'mm',
	op1to2FindPartClearanceHeight: 'mm',
	op2to3TransferHeight: 'mm',
	xViseTransferOffset: 'mm',
	yViseTransferOffset: 'mm',
	xViseTransferOffsetOp2: 'mm',
	yViseTransferOffsetOp2: 'mm',
	xViseTransferOffsetOp3: 'mm',
	yViseTransferOffsetOp3: 'mm',
	partDetectionTolerance: 'mm',
};

const INCH_DECIMALS = 4;

const isNumber = (value: unknown): value is number =>
	typeof value === 'number' && Number.isFinite(value);

export const toInches = (value: number, unit: MetricUnit) =>
	Number((value / PER_INCH[unit]).toFixed(INCH_DECIMALS));

export const fromInches = (value: number, unit: MetricUnit) =>
	value * PER_INCH[unit];

export const formatLength = (
	key: string,
	value: unknown,
	useInches: boolean
) => {
	if (!isNumber(value)) {
		return '—';
	}
	const unit = LENGTH_PARAMETER_UNITS[key] ?? 'mm';
	return useInches ? toInches(value, unit).toFixed(3) : value.toFixed(2);
};

const mapLengths = (
	json: VSCPartJson,
	convert: (key: string, value: number, unit: MetricUnit) => number
): VSCPartJson => {
	if (!json.parameters || typeof json.parameters !== 'object') {
		return json;
	}
	const parameters = { ...json.parameters };
	for (const [key, unit] of Object.entries(LENGTH_PARAMETER_UNITS)) {
		const value = parameters[key];
		if (isNumber(value)) {
			parameters[key] = convert(key, value, unit);
		}
	}
	return { ...json, parameters };
};

export const jsonToInches = (json: VSCPartJson) =>
	mapLengths(json, (_key, value, unit) => toInches(value, unit));

/**
 * Inverse of `jsonToInches`. A length the user left alone keeps its exact
 * stored metric value instead of the rounded-inch round trip, so opening and
 * saving a program never drifts it.
 */
export const jsonToMetric = (inchJson: VSCPartJson, original: VSCPartJson) =>
	mapLengths(inchJson, (key, value, unit) => {
		const originalValue = original.parameters?.[key];
		if (isNumber(originalValue) && toInches(originalValue, unit) === value) {
			return originalValue;
		}
		return value * PER_INCH[unit];
	});
